import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Container,
  Stack,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Button,
  Grid,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,IconButton,
  Box,
} from "@mui/material";
import { ZoomIn, ZoomOut } from "@mui/icons-material";
import Scrollbar from "../components/Scrollbar";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getVehicleReports } from "src/api/vehicles";
import * as XLSX from "xlsx";
import Page from "src/components/Page";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";

const LicensePlateRecognitionReport = () => {
  const [vehicleData, setVehicleData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [page, setPage] = useState(0);

  const [selectedImage, setSelectedImage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);


  const [fromDate, setFromDate] = useState(dayjs().startOf("day"));
  const [toDate, setToDate] = useState(dayjs().endOf("day"));


  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [start, setStart] = useState({ x: 0, y: 0 });

  // Zoom In and Out
  const handleZoom = (delta) => {
    setZoom((prev) => Math.max(1, Math.min(prev + delta, 3))); // Limit zoom between 1x to 3x
  };

  // Handle Mouse Scroll for Zoom
  const handleWheel = (e) => {
    e.preventDefault();
    handleZoom(e.deltaY < 0 ? 0.1 : -0.1);
  };

  // Handle Mouse Drag
  const handleMouseDown = (e) => {
    setDragging(true);
    setStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e) => {
    if (dragging) {
      setPosition({ x: e.clientX - start.x, y: e.clientY - start.y });
    }
  };

  const handleMouseUp = () => {
    setDragging(false);
  };



  const fetchVehicleReports = (fromDate, toDate) => {
    const body = {
      from_date: fromDate.format("YYYY-MM-DD HH:mm:ss"),
      to_date: toDate.format("YYYY-MM-DD HH:mm:ss"),
    };

    getVehicleReports(body, (response) => {
      if (response.status === 200 && response.data) {
        const validData = response.data.filter(item => item.ObjectEntryOn !== null && item.ObjectEntryOn !== undefined);
        setVehicleData(validData);
        setFilteredData(validData);
        setPage(0); 
      }
    });
  };

  useEffect(() => {
    fetchVehicleReports(fromDate, toDate);
  }, []);
  

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchText(query);

    const filtered = vehicleData.filter((item) =>
      item.LicensePlateNumber?.toLowerCase().includes(query)
    );

    setFilteredData(filtered);
  };


  const handleGetReports = () => {
    fetchVehicleReports(fromDate, toDate);
  };

  const handleExportExcel = () => {
    if (!vehicleData || vehicleData.length === 0) {
      alert("No data to Export.");
      return;
    }

    const headers = [
      "S.No",
      "Vehicle Number",
      "Entry Time",
      "Image",
    ];

    const formattedData = vehicleData.map((item, index) => ({
      "S.No": index + 1,
      "Vehicle Number": item.LicensePlateNumber
        ? item.LicensePlateNumber.toUpperCase()
        : "",
      "Entry Time": item.ObjectEntryOn || "",
      "Image": item.SnapshotEntryUrl || "",
    }));


    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(formattedData, { header: headers });
    XLSX.utils.book_append_sheet(wb, ws, "License Plate Recognition Report");
    XLSX.writeFile(wb, `License_Plate_Recognition_Report_${new Date().toISOString()}.xlsx`);
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedImage(null);
    setZoom(1); // Reset zoom to original size
    setPosition({ x: 0, y: 0 }); // Reset position to original
  };
  const handleRest =() =>{
    setZoom(1); // Reset zoom to original size
    setPosition({ x: 0, y: 0 }); // Reset position to original
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page
  };

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Page title="License Plate Recognition Report">
      <Container maxWidth="xxl" sx={{p:0}}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">License Plate Recognition Report</Typography>
        </Stack>

        <Card>
          <CardContent>
            <Grid container spacing={2} alignItems="center" mb={1}>
              <Grid container spacing={2} sx={{ p: 2 }} alignItems="center" mb={1}>
                   <Grid item>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="From Date"
                            value={fromDate}
                            onChange={(newValue) => setFromDate(newValue)}
                            maxDate={dayjs()}
                            renderInput={(params) => <TextField {...params} fullWidth />}
                          />
                      </LocalizationProvider>
                </Grid>

                <Grid item>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="To Date"
                      value={toDate}
                      onChange={(newValue) => setToDate(newValue)}
                      minDate={fromDate}
                      maxDate={dayjs()}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </LocalizationProvider>
                </Grid>


                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGetReports}
                    startIcon={<InsertDriveFileIcon />}
                  >
                    Get Report
                  </Button>
                </Grid>

                {/* <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleExportExcel}
                    startIcon={<InsertDriveFileIcon />}
                  >
                    Export to Excel
                  </Button>
                </Grid> */}
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ p: 2 }} alignItems="center" mb={1}>
              <Grid item xs={4}>
                <TextField
                  label="Search by Vehicle Number"
                  variant="outlined"
                  value={searchText}
                  onChange={handleSearch}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Scrollbar>
              <TableContainer>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>S.No</TableCell>
                      <TableCell>Vehicle Number</TableCell>
                      <TableCell>Entry Time</TableCell>
                      <TableCell>Image</TableCell>
                      {/* <TableCell>Vehicle Out Image</TableCell>
                      <TableCell>Vehicle Out Time</TableCell>
                      <TableCell>Vehicle Duration</TableCell> */}
                    </TableRow>
                  </TableHead>
                   <TableBody>
                    {paginatedData.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>                    
                        <TableCell sx={{ textTransform: "uppercase" }} align="left">{item?.LicensePlateNumber}</TableCell>
                        <TableCell>
                           {item.ObjectEntryOn
                             ? item.ObjectEntryOn.split(".")[0].replace("T", " ")
                             : "N/A"}
                         </TableCell>
                        <TableCell>
                          {item?.SnapshotEntryUrl ? (
                            <img
                              src={item.SnapshotEntryUrl}
                              alt="Snapshot"
                              style={{ width: 50, cursor: "pointer" }}
                              onClick={() => handleImageClick(item.SnapshotEntryUrl)}
                            />
                          ) : (
                            "N/A"
                          )}
                        </TableCell>
                         {/* 
                         <TableCell>
                          {item?.SnapshotExitUrl ? (
                            <img
                              src={item.SnapshotExitUrl}
                              alt="Snapshot"
                              style={{ width: 50, cursor: "pointer" }}
                              onClick={() => handleImageClick(item.SnapshotExitUrl)}
                            />
                          ) : (
                            "N/A"
                          )}
                        </TableCell>
                        <TableCell>{item.ObjectExitOn
                             ? item.ObjectExitOn.split(".")[0].replace("T", " ")
                             : "N/A"}</TableCell>
                        <TableCell> {item?.duration || "00:00"}</TableCell> */}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardContent>
        </Card>

        {/* Dialog for Image Preview */}
        {/* <Dialog open={dialogOpen} onClose={handleCloseDialog}>
          <DialogTitle>Image Preview</DialogTitle>
          <DialogContent>
            {selectedImage && (
              <Box component="img" src={selectedImage} alt="Snapshot" sx={{ width: "100%" }} />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="error"  variant="contained">
            Close
            </Button>
          </DialogActions>
        </Dialog> */}
    <Dialog
      aria-labelledby="image-preview-dialog"
      open={dialogOpen}
      onClose={handleCloseDialog}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle id="image-preview-dialog">Image Preview</DialogTitle>
      <DialogContent
        // sx={{
        //   display: "flex",
        //   justifyContent: "center",
        //   alignItems: "center",
        //   overflow: "hidden",
        //   position: "relative",
        //   cursor: dragging ? "grabbing" : "grab",
        // }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >

      {selectedImage && (
          <Box component="img" src={selectedImage} alt="Snapshot" sx={{ width: "100%", maxHeight: "80vh", transform: `scale(${zoom}) translate(${position.x}px, ${position.y}px)`,
          transition: dragging ? "none" : "transform 0.3s ease-in-out"}} />
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        {/* Zoom Controls */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={() => handleZoom(-0.2)} disabled={zoom <= 1}>
            <ZoomOut />
          </IconButton>
          <Typography variant="body2">{Math.round(zoom * 100)}%</Typography>
          <IconButton onClick={() => handleZoom(0.2)} disabled={zoom >= 3}>
            <ZoomIn />
          </IconButton>
            <Button onClick={handleRest} variant="contained" color="primary">
            reset
          </Button>
        </Box>
        {/* Close Button */}
        <Button onClick={handleCloseDialog} color="error" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>


      </Container>
    </Page>
  );
};

export default LicensePlateRecognitionReport;

